import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Trash2, ArrowUp, ArrowDown, Plus, Image as ImageIcon } from 'lucide-react';
import { Dropzone } from './Dropzone';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';

const uid = () => Math.random().toString(36).slice(2, 10);

const emptySlide = () => ({
  id: uid(),
  src: '',
  title: '',
  subtitle: '',
  fit: 'contain',
  published: true,
});

const HeroSlidesManager = () => {
  const [data, setData] = useState({ id: 'hero_slides', slides: [], interval_ms: 9000, updated_at: '' });
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      const res = await adminApi.get('/hero-slides');
      if (res.data) {
        const slides = (res.data.slides || []).map((s) => ({ ...emptySlide(), ...s, id: s.id || uid() }));
        setData({ ...res.data, slides });
      }
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const setSlides = (slides) => setData((d) => ({ ...d, slides }));

  const addSlide = () => setSlides([...data.slides, emptySlide()]);

  const removeSlide = (idx) => {
    if (!window.confirm('Remove this slide? This cannot be undone once saved.')) return;
    setSlides(data.slides.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= data.slides.length) return;
    const next = [...data.slides];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSlides(next);
  };

  const updateSlide = (idx, patch) => {
    setSlides(data.slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const save = async () => {
    if (!loaded) return;
    // Guard: at least one slide with src
    const clean = data.slides.filter((s) => (s.src || '').trim());
    if (clean.length === 0) {
      toast({ title: 'Add at least one image', description: 'A slide must have an image.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.post('/hero-slides', { ...data, slides: clean });
      await fetchData();
      toast({ title: 'Saved', description: `${clean.length} slide(s) saved` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="hero-slides-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold">Hero Slider Images</h2>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <div className="flex items-center gap-2">
          <HistoryDialog
            collection="hero_slides"
            docId="hero_slides"
            onRestored={fetchData}
          />
          <Button onClick={addSlide} variant="outline" data-testid="hero-slides-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Slide
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Slider Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="max-w-xs">
            <Label>Autoplay interval (ms)</Label>
            <Input
              type="number"
              min={2000}
              step={500}
              value={data.interval_ms || 9000}
              onChange={(e) => setData({ ...data, interval_ms: Number(e.target.value) || 9000 })}
              data-testid="hero-slides-interval"
            />
            <p className="text-xs text-gray-500 mt-1">Time each slide is shown before advancing. Default 9000 (9s).</p>
          </div>
        </CardContent>
      </Card>

      {data.slides.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-gray-500">
            <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No slides yet. Click <b>Add Slide</b> to create your first image.</p>
          </CardContent>
        </Card>
      )}

      {data.slides.map((slide, idx) => (
        <Card key={slide.id} data-testid={`hero-slide-card-${idx}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Slide #{idx + 1}</CardTitle>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`hero-slide-up-${idx}`}>
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === data.slides.length - 1} data-testid={`hero-slide-down-${idx}`}>
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => removeSlide(idx)} data-testid={`hero-slide-remove-${idx}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label>Image</Label>
                {slide.src ? (
                  <div className="relative bg-[#0A1628] rounded-md overflow-hidden aspect-video">
                    <img
                      src={slide.src}
                      alt={slide.title}
                      className={`w-full h-full ${slide.fit === 'cover' ? 'object-cover' : 'object-contain'}`}
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-sm">
                    No image
                  </div>
                )}
                <Dropzone
                  compact
                  onUpload={(url) => updateSlide(idx, { src: url })}
                  label={slide.src ? 'Replace image' : 'Upload image'}
                />
                <div className="flex items-center gap-3 pt-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-xs">Fit</Label>
                    <select
                      value={slide.fit}
                      onChange={(e) => updateSlide(idx, { fit: e.target.value })}
                      className="px-2 py-1 border rounded text-sm"
                      data-testid={`hero-slide-fit-${idx}`}
                    >
                      <option value="contain">Contain (letterboxed, no crop)</option>
                      <option value="cover">Cover (fills, may crop)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Published</Label>
                    <Switch
                      checked={slide.published !== false}
                      onCheckedChange={(v) => updateSlide(idx, { published: v })}
                      data-testid={`hero-slide-published-${idx}`}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Caption Title</Label>
                  <Input
                    value={slide.title}
                    onChange={(e) => updateSlide(idx, { title: e.target.value })}
                    placeholder="e.g. EWRF Charity Golf 2026"
                    data-testid={`hero-slide-title-${idx}`}
                  />
                </div>
                <div>
                  <Label>Caption Subtitle</Label>
                  <Textarea
                    rows={4}
                    value={slide.subtitle}
                    onChange={(e) => updateSlide(idx, { subtitle: e.target.value })}
                    placeholder="Short supporting line shown below the title"
                    data-testid={`hero-slide-subtitle-${idx}`}
                  />
                </div>
                <div>
                  <Label>Image URL (optional)</Label>
                  <Input
                    value={slide.src}
                    onChange={(e) => updateSlide(idx, { src: e.target.value })}
                    placeholder="Paste a URL or upload above"
                    data-testid={`hero-slide-src-${idx}`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-4 z-10">
        <Button onClick={save} disabled={loading || !loaded} className="bg-[#1565C0] shadow-lg" data-testid="save-hero-slides-btn">
          <Save className="w-4 h-4 mr-2" />{!loaded ? 'Loading…' : loading ? 'Saving…' : 'Save Hero Slides'}
        </Button>
      </div>
    </div>
  );
};

export default HeroSlidesManager;
